import moment from 'moment-timezone';

import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

export enum AuditLogFormat {
  CSV = 'CSV',
  PDF = 'PDF',
}

export interface ExportAuditLogsResponse {
  url: string;
}

export interface ExportAuditLogsDeferredResponse {
  url?: string; // Optional URL if ready immediately
  message?: string; // Optional message about email notification
}

export interface ExportHistoryReportResponse {
  succeeded: boolean;
  url: string;
  message: string;
}

export enum DisallowedMoveReason {
  ENABLED_TO_DISABLED = 'enabled project to disabled project',
  DISABLED_TO_ENABLED = 'disabled project to enabled project',
}

export interface CheckFolderMoveAllowedByDataModsResponse {
  move_allowed: boolean;
  data_mods_reason_required: boolean;
  disallowed_api_ids: {[apiId: string]: DisallowedMoveReason};
}

export interface AuditLogRowData {
  [key: string]: string;
}

export interface AuditLogPagination {
  total: number;
  pageSize: number;
  pageOffset: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface AuditLogObjectMetadata {
  title: string;
}

export interface AuditLogsDataResponse {
  columns: Array<string>;
  data: Array<AuditLogRowData>;
  pagination: AuditLogPagination;
  objectMetadata: AuditLogObjectMetadata;
}

export interface AuditFilterUser {
  id: string;
  name: string;
  handle: string;
}

export interface ActionFilterCriteria {
  effectType: string;
  changedProperty: string | null;
  affectedObjectType: string | null;
  action: string | null;
}

export interface AuditFilterAction {
  displayName: string;
  filters: Array<ActionFilterCriteria>;
}

export interface AuditFilterOptionsResponse {
  users: Array<AuditFilterUser>;
  actions: Array<AuditFilterAction>;
  reasonCodes: Array<string> | null;
  displayTimezone: string;
  displayDateFormat: string;
}

export function exportAuditLogs(
  ids: Array<string>,
  format: AuditLogFormat,
  additionalOptions: {resultsWarehouseIds?: Array<string>} = {}
): JQueryPromise<ExportAuditLogsDeferredResponse> {
  return TaskRunner.postJSON({
    data: {ids, format, ...additionalOptions, browser_timezone: moment.tz.guess()},
    url: '/1/api/audits:export-logs',
  });
}

export function exportRelatedResultsAuditLogs(
  id: string,
  format: AuditLogFormat
): JQueryPromise<ExportAuditLogsDeferredResponse> {
  return TaskRunner.postJSON({
    data: {apiId: id, exportFormat: format},
    url: '/1/api/audits:export-related-results-logs',
  });
}

export function exportSampleContainerHistory(
  containerApiIds: Array<string>,
  exportFormat: AuditLogFormat
): JQueryPromise<ExportHistoryReportResponse> {
  return TaskRunner.postJSON({
    data: {
      container_api_ids: containerApiIds,
      export_format: exportFormat,
      browser_timezone: moment.tz.guess(),
    },
    url: '/1/api/audits:export-sample-container-history-report',
  });
}

export function exportPlateHistory(
  plateApiIds: Array<string>,
  exportFormat: AuditLogFormat
): JQueryPromise<ExportHistoryReportResponse> {
  return TaskRunner.postJSON({
    data: {
      plate_api_ids: plateApiIds,
      export_format: exportFormat,
      browser_timezone: moment.tz.guess(),
    },
    url: '/1/api/audits:export-plate-history-report',
  });
}

export function exportDataModsTACReport(format: AuditLogFormat): JQueryPromise<ExportAuditLogsResponse> {
  return TaskRunner.postJSON({
    data: {export_format: format, browser_timezone: moment.tz.guess()},
    url: '/1/api/audits:export-data-mods-settings-logs',
  });
}

export function exportCreditLimitTACReport(format: AuditLogFormat): JQueryPromise<ExportAuditLogsResponse> {
  return TaskRunner.postJSON({
    data: {export_format: format, browser_timezone: moment.tz.guess()},
    url: '/1/api/audits:export-credit-limit-audit-logs',
  });
}

export function exportLocationHistoryReport(
  storableApiIds: Array<string>,
  exportFormat: AuditLogFormat,
  startTime?: string,
  endTime?: string
): Promise<TaskRunner.OngoingTaskResponse> {
  return net.postJSONPromise({
    data: {
      storable_api_ids: storableApiIds,
      export_format: exportFormat,
      start_time: startTime,
      end_time: endTime,
      browser_timezone: moment.tz.guess(),
    },
    url: '/1/api/audits:export-location-history-report',
  });
}

export function exportIPAllowlistsTACReport(
  format: AuditLogFormat
): JQueryPromise<ExportAuditLogsDeferredResponse> {
  return TaskRunner.postJSON({
    data: {export_format: format, browser_timezone: moment.tz.guess()},
    url: '/1/api/tenant:export-ip-allowlists-audit-logs',
  });
}

export function exportAISettingsTACReport(format: AuditLogFormat): JQueryPromise<ExportAuditLogsResponse> {
  return TaskRunner.postJSON({
    data: {export_format: format, browser_timezone: moment.tz.guess()},
    url: '/1/api/tenant:export-ai-settings-audit-logs',
  });
}

export function checkDataModsReasonRequired(
  apiIds?: Array<string>
): Promise<{data_mods_reason_required: boolean}> {
  return net.postJSONPromise({
    data: {api_ids: apiIds},
    url: '/1/api/audits:check-data-mods-reason-required',
  });
}

export function checkFolderMoveAllowedByDataMods(
  apiIds: Array<string | null>,
  destinationFolderApiId: string | null
): Promise<CheckFolderMoveAllowedByDataModsResponse> {
  apiIds = apiIds.filter((apiId) => typeof apiId === 'string');
  destinationFolderApiId = destinationFolderApiId ?? '';
  return net.postJSONPromise({
    data: {api_ids: apiIds, destination_folder_api_id: destinationFolderApiId},
    url: '/1/api/audits:check-folder-move-allowed-by-data-mods',
  });
}

export function exportTenantCapabilitiesReport(
  format: AuditLogFormat
): JQueryPromise<ExportAuditLogsResponse> {
  return TaskRunner.postJSON({
    data: {export_format: format, browser_timezone: moment.tz.guess()},
    url: '/1/api/audits:export-tenant-capabilities-audit-logs',
  });
}

export function getFilterOptions(
  apiIds: Array<string>,
  options: {
    resultsWarehouseIds?: Array<string>;
    relatedResultsModelId?: string;
  } = {}
): Promise<AuditFilterOptionsResponse> {
  const params = new URLSearchParams();
  if (apiIds.length > 0) {
    params.set('apiIds', apiIds.join(','));
  }
  if (options.resultsWarehouseIds && options.resultsWarehouseIds.length > 0) {
    params.set('resultsWarehouseIds', options.resultsWarehouseIds.join(','));
  }
  if (options.relatedResultsModelId) {
    params.set('relatedResultsModelId', options.relatedResultsModelId);
  }
  params.set('browserTimezone', moment.tz.guess());
  return net.getJSONPromise(`/1/api/audits:get-filter-options?${params.toString()}`);
}

/** Report-scoped previews query a dedicated endpoint by report type rather than by object api id. */
export type AuditLogReportType = 'creditLimit' | 'authenticationOptions';

const REPORT_PREVIEW_URLS: Record<AuditLogReportType, string> = {
  creditLimit: '/1/api/audits:get-credit-limit-paginated-data',
  authenticationOptions: '/1/api/audits:get-authentication-options-paginated-data',
};

export function getAuditLogsData(
  apiIds: Array<string>,
  options: {
    columnNames?: Array<string>;
    resultsWarehouseIds?: Array<string>;
    relatedResultsModelId?: string;
    pageSize?: number;
    pageOffset?: number;
    userIds?: Array<string>;
    actions?: Array<ActionFilterCriteria>;
    reasonCodes?: Array<string>;
    startDate?: string;
    endDate?: string;
    sortAscending?: boolean;
    reportType?: AuditLogReportType;
  } = {}
): Promise<AuditLogsDataResponse> {
  if (options.reportType != null) {
    // Selection is server-side (apiIds/filters do not apply); only pagination is threaded.
    return net.postJSONPromise({
      data: {
        pageSize: options.pageSize,
        pageOffset: options.pageOffset,
        browserTimezone: moment.tz.guess(),
        sortAscending: options.sortAscending,
      },
      url: REPORT_PREVIEW_URLS[options.reportType],
    });
  }
  return net.postJSONPromise({
    data: {
      apiIds,
      columnNames: options.columnNames,
      resultsWarehouseIds: options.resultsWarehouseIds,
      relatedResultsModelId: options.relatedResultsModelId,
      pageSize: options.pageSize,
      pageOffset: options.pageOffset,
      browserTimezone: moment.tz.guess(),
      userIds: options.userIds,
      actions: options.actions,
      reasonCodes: options.reasonCodes,
      startDate: options.startDate,
      endDate: options.endDate,
      sortAscending: options.sortAscending,
    },
    url: '/1/api/audits:get-paginated-data',
  });
}

export function exportAuthenticationOptionsReport(
  format: AuditLogFormat
): JQueryPromise<ExportAuditLogsResponse> {
  return TaskRunner.postJSON({
    data: {export_format: format, browser_timezone: moment.tz.guess()},
    url: '/1/api/audits:export-authentication-options-audit-logs',
  });
}
